import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Loader2, Plus, Trash2, Download, FileText, Users, Building2,
  CheckCircle, XCircle, Eye, Edit2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GST_RATES = [0, 5, 12, 18, 28];

const emptyItem = { description: '', hsn_code: '', quantity: 1, rate: '', gst_percent: 18 };

export default function GSTInvoicing() {
  const [activeTab, setActiveTab] = useState('invoices');

  // Clients
  const [clients, setClients] = useState([]);
  const [clientForm, setClientForm] = useState({ company_name: '', gstin: '', address: '', city: '', state: '', pincode: '', contact_person: '', phone: '', email: '' });
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    client_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', notes: '',
    items: [{ ...emptyItem }],
  });
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({});

  // Invoice detail
  const [viewingInvoice, setViewingInvoice] = useState(null);

  useEffect(() => { loadClients(); loadInvoices(); }, []);

  const loadClients = async () => {
    try { const r = await api.get('/clients'); setClients(r.data); } catch { toast.error('Failed to load clients'); }
  };
  const loadInvoices = async () => {
    try { const r = await api.get('/invoices'); setInvoices(r.data); } catch { toast.error('Failed to load invoices'); }
  };

  // ── Client CRUD ──
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setClientLoading(true);
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient}`, clientForm);
        toast.success('Client updated');
      } else {
        await api.post('/clients', clientForm);
        toast.success('Client added');
      }
      setShowClientForm(false);
      setEditingClient(null);
      setClientForm({ company_name: '', gstin: '', address: '', city: '', state: '', pincode: '', contact_person: '', phone: '', email: '' });
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save client');
    } finally { setClientLoading(false); }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try { await api.delete(`/clients/${id}`); toast.success('Client deleted'); loadClients(); }
    catch { toast.error('Failed to delete'); }
  };

  const startEditClient = (c) => {
    setClientForm({ company_name: c.company_name, gstin: c.gstin || '', address: c.address, city: c.city, state: c.state, pincode: c.pincode, contact_person: c.contact_person, phone: c.phone, email: c.email || '' });
    setEditingClient(c.client_id);
    setShowClientForm(true);
  };

  // ── Invoice ──
  const addItem = () => setInvoiceForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (idx) => setInvoiceForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, val) => {
    setInvoiceForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: val };
      return { ...f, items };
    });
  };

  const calcSubtotal = () => invoiceForm.items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0), 0);
  const calcGST = () => invoiceForm.items.reduce((sum, it) => {
    const taxable = (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
    return sum + taxable * (parseFloat(it.gst_percent) || 0) / 100;
  }, 0);

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceForm.client_id) { toast.error('Select a client'); return; }
    if (invoiceForm.items.length === 0) { toast.error('Add at least one item'); return; }
    for (let i = 0; i < invoiceForm.items.length; i++) {
      if (!invoiceForm.items[i].description || !invoiceForm.items[i].rate) {
        toast.error(`Item ${i + 1}: description and rate are required`); return;
      }
    }
    setInvoiceLoading(true);
    try {
      const payload = {
        ...invoiceForm,
        items: invoiceForm.items.map(it => ({
          ...it,
          quantity: parseFloat(it.quantity) || 1,
          rate: parseFloat(it.rate) || 0,
          gst_percent: parseFloat(it.gst_percent) || 18,
        })),
      };
      await api.post('/invoices', payload);
      toast.success('Invoice created');
      setShowInvoiceForm(false);
      setInvoiceForm({ client_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', notes: '', items: [{ ...emptyItem }] });
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally { setInvoiceLoading(false); }
  };

  const downloadPDF = async (invoiceNum) => {
    setPdfLoading(p => ({ ...p, [invoiceNum]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${invoiceNum}/pdf`, { credentials: 'include' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNum}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setPdfLoading(p => ({ ...p, [invoiceNum]: false })); }
  };

  const markInvoiceStatus = async (num, status) => {
    try {
      await api.patch(`/invoices/${num}/status`, { status });
      toast.success(`Invoice marked as ${status}`);
      loadInvoices();
      if (viewingInvoice?.invoice_number === num) setViewingInvoice(v => ({ ...v, status }));
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div data-testid="gst-invoicing-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">GST Invoicing</h1>
        <p className="text-gray-600">Manage B2B clients and generate GST tax invoices</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit" data-testid="invoice-tabs">
        {[
          { key: 'invoices', label: 'Invoices', icon: FileText },
          { key: 'clients', label: 'Clients', icon: Building2 },
        ].map(t => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setShowClientForm(false); setShowInvoiceForm(false); setViewingInvoice(null); }}
            data-testid={`tab-${t.key}`}
            className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${activeTab === t.key ? 'bg-white text-shizah-blue shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
            <t.icon size={16} />{t.label}
          </button>
        ))}
      </div>

      {/* ──── CLIENTS TAB ──── */}
      {activeTab === 'clients' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Your Clients</h2>
            <Button onClick={() => { setShowClientForm(true); setEditingClient(null); setClientForm({ company_name: '', gstin: '', address: '', city: '', state: '', pincode: '', contact_person: '', phone: '', email: '' }); }}
              data-testid="add-client-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
              <Plus size={18} className="mr-2" />Add Client
            </Button>
          </div>

          {/* Client Form Modal */}
          {showClientForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="client-form-modal">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{editingClient ? 'Edit Client' : 'Add New Client'}</h3>
                <form onSubmit={handleClientSubmit} className="space-y-3">
                  <div><Label>Company Name *</Label><Input value={clientForm.company_name} onChange={e => setClientForm({ ...clientForm, company_name: e.target.value })} required data-testid="client-company-name" /></div>
                  <div><Label>GSTIN</Label><Input value={clientForm.gstin} onChange={e => setClientForm({ ...clientForm, gstin: e.target.value })} placeholder="22AAAAA0000A1Z5" data-testid="client-gstin" /></div>
                  <div><Label>Address *</Label><Input value={clientForm.address} onChange={e => setClientForm({ ...clientForm, address: e.target.value })} required data-testid="client-address" /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>City *</Label><Input value={clientForm.city} onChange={e => setClientForm({ ...clientForm, city: e.target.value })} required /></div>
                    <div><Label>State *</Label><Input value={clientForm.state} onChange={e => setClientForm({ ...clientForm, state: e.target.value })} required /></div>
                    <div><Label>Pincode *</Label><Input value={clientForm.pincode} onChange={e => setClientForm({ ...clientForm, pincode: e.target.value })} required /></div>
                  </div>
                  <div><Label>Contact Person *</Label><Input value={clientForm.contact_person} onChange={e => setClientForm({ ...clientForm, contact_person: e.target.value })} required data-testid="client-contact" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Phone *</Label><Input value={clientForm.phone} onChange={e => setClientForm({ ...clientForm, phone: e.target.value })} required data-testid="client-phone" /></div>
                    <div><Label>Email</Label><Input value={clientForm.email} onChange={e => setClientForm({ ...clientForm, email: e.target.value })} type="email" /></div>
                  </div>
                  <div className="flex justify-end gap-3 pt-3">
                    <Button type="button" variant="ghost" onClick={() => { setShowClientForm(false); setEditingClient(null); }}>Cancel</Button>
                    <Button type="submit" disabled={clientLoading} className="bg-shizah-blue hover:bg-blue-700 text-white" data-testid="save-client-button">
                      {clientLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}{editingClient ? 'Update' : 'Save'} Client
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Client List */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {clients.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No clients yet. Add your first B2B client.</div>
            ) : (
              <table className="w-full" data-testid="clients-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">GSTIN</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">City</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients.map(c => (
                    <tr key={c.client_id} className="hover:bg-gray-50" data-testid={`client-row-${c.client_id}`}>
                      <td className="px-6 py-3 text-sm font-semibold text-gray-900">{c.company_name}</td>
                      <td className="px-6 py-3 text-sm text-gray-600 font-mono">{c.gstin || '-'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{c.contact_person}<br /><span className="text-xs">{c.phone}</span></td>
                      <td className="px-6 py-3 text-sm text-gray-600">{c.city}, {c.state}</td>
                      <td className="px-6 py-3 text-sm">
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => startEditClient(c)} className="text-shizah-blue"><Edit2 size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteClient(c.client_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ──── INVOICES TAB ──── */}
      {activeTab === 'invoices' && !showInvoiceForm && !viewingInvoice && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Invoices</h2>
            <Button onClick={() => setShowInvoiceForm(true)} data-testid="create-invoice-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
              <Plus size={18} className="mr-2" />Create Invoice
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {invoices.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No invoices yet. Create your first GST invoice.</div>
            ) : (
              <table className="w-full" data-testid="invoices-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoices.map(inv => (
                    <tr key={inv.invoice_number} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                      <td className="px-6 py-3 text-sm font-semibold text-shizah-blue">{inv.invoice_number}</td>
                      <td className="px-6 py-3 text-sm text-gray-900">{inv.client?.company_name || inv.client_id}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{inv.invoice_date}</td>
                      <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">&#8377;{inv.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="px-6 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${inv.status === 'paid' ? 'bg-green-100 text-green-800' : inv.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {inv.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setViewingInvoice(inv)} data-testid={`view-invoice-${inv.invoice_number}`} className="text-shizah-blue">
                            <Eye size={14} className="mr-1" />View
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadPDF(inv.invoice_number)} disabled={pdfLoading[inv.invoice_number]} data-testid={`download-pdf-${inv.invoice_number}`} className="text-gray-700">
                            {pdfLoading[inv.invoice_number] ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                          </Button>
                          {inv.status === 'unpaid' && (
                            <Button size="sm" variant="ghost" onClick={() => markInvoiceStatus(inv.invoice_number, 'paid')} className="text-green-600"><CheckCircle size={14} /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ──── INVOICE DETAIL VIEW ──── */}
      {activeTab === 'invoices' && viewingInvoice && !showInvoiceForm && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Button variant="ghost" onClick={() => setViewingInvoice(null)} data-testid="back-to-invoices">← Back to Invoices</Button>
            <div className="flex gap-2">
              <Button onClick={() => downloadPDF(viewingInvoice.invoice_number)} disabled={pdfLoading[viewingInvoice.invoice_number]}
                data-testid="detail-download-pdf" className="bg-shizah-blue hover:bg-blue-700 text-white">
                {pdfLoading[viewingInvoice.invoice_number] ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download size={16} className="mr-2" />}Download PDF
              </Button>
              {viewingInvoice.status === 'unpaid' && (
                <>
                  <Button onClick={() => markInvoiceStatus(viewingInvoice.invoice_number, 'paid')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="mark-paid-button">
                    <CheckCircle size={16} className="mr-2" />Mark Paid
                  </Button>
                  <Button onClick={() => markInvoiceStatus(viewingInvoice.invoice_number, 'cancelled')} variant="outline" className="border-red-500 text-red-600" data-testid="cancel-invoice-button">
                    <XCircle size={16} className="mr-2" />Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-8" data-testid="invoice-detail-view">
            <div className="flex justify-between items-start mb-8">
              <div>
                <h2 className="text-2xl font-black text-shizah-blue">Shizah Mobility</h2>
                <p className="text-sm text-gray-500">Your Comfort, Our Priority</p>
              </div>
              <div className="text-right">
                <h3 className="text-xl font-bold text-gray-900">TAX INVOICE</h3>
                <p className="text-sm text-gray-600">{viewingInvoice.invoice_number}</p>
                <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold ${viewingInvoice.status === 'paid' ? 'bg-green-100 text-green-800' : viewingInvoice.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {viewingInvoice.status?.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Bill To</p>
                <p className="font-semibold text-gray-900">{viewingInvoice.client?.company_name}</p>
                <p className="text-sm text-gray-600">{viewingInvoice.client?.address}, {viewingInvoice.client?.city}</p>
                <p className="text-sm text-gray-600">{viewingInvoice.client?.state} - {viewingInvoice.client?.pincode}</p>
                {viewingInvoice.client?.gstin && <p className="text-sm text-gray-600 mt-1">GSTIN: {viewingInvoice.client.gstin}</p>}
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600"><strong>Date:</strong> {viewingInvoice.invoice_date}</p>
                {viewingInvoice.due_date && <p className="text-sm text-gray-600"><strong>Due:</strong> {viewingInvoice.due_date}</p>}
              </div>
            </div>

            <table className="w-full mb-6">
              <thead className="bg-shizah-blue text-white">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-bold">#</th>
                  <th className="px-4 py-2 text-left text-xs font-bold">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-bold">HSN</th>
                  <th className="px-4 py-2 text-right text-xs font-bold">Qty</th>
                  <th className="px-4 py-2 text-right text-xs font-bold">Rate</th>
                  <th className="px-4 py-2 text-right text-xs font-bold">GST</th>
                  <th className="px-4 py-2 text-right text-xs font-bold">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {viewingInvoice.items?.map((it, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{i + 1}</td>
                    <td className="px-4 py-2 text-sm">{it.description}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{it.hsn_code || '-'}</td>
                    <td className="px-4 py-2 text-sm text-right">{it.quantity}</td>
                    <td className="px-4 py-2 text-sm text-right">&#8377;{it.rate?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2 text-sm text-right">{it.gst_percent}%</td>
                    <td className="px-4 py-2 text-sm text-right font-semibold">&#8377;{it.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>&#8377;{viewingInvoice.subtotal?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between text-sm"><span>Total GST</span><span>&#8377;{viewingInvoice.total_gst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between text-lg font-bold text-shizah-blue border-t pt-2"><span>Grand Total</span><span>&#8377;{viewingInvoice.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
              </div>
            </div>

            {viewingInvoice.notes && (
              <div className="mt-6 pt-4 border-t">
                <p className="text-xs font-bold text-gray-500 uppercase mb-1">Notes</p>
                <p className="text-sm text-gray-600">{viewingInvoice.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ──── CREATE INVOICE FORM ──── */}
      {activeTab === 'invoices' && showInvoiceForm && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Create New Invoice</h2>
            <Button variant="ghost" onClick={() => setShowInvoiceForm(false)}>Cancel</Button>
          </div>

          <form onSubmit={handleInvoiceSubmit} className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4">Invoice Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Client *</Label>
                  <Select value={invoiceForm.client_id} onValueChange={v => setInvoiceForm({ ...invoiceForm, client_id: v })}>
                    <SelectTrigger data-testid="select-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Invoice Date *</Label>
                  <Input type="date" value={invoiceForm.invoice_date} onChange={e => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} required data-testid="invoice-date" />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} data-testid="invoice-due-date" />
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-gray-900">Line Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-button">
                  <Plus size={14} className="mr-1" />Add Item
                </Button>
              </div>

              <div className="space-y-3">
                {/* Header */}
                <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-bold text-gray-500 uppercase px-1">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2">HSN Code</div>
                  <div>Qty</div>
                  <div className="col-span-2">Rate (INR)</div>
                  <div className="col-span-2">GST %</div>
                  <div></div>
                </div>

                {invoiceForm.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-item-${idx}`}>
                    <div className="col-span-12 md:col-span-4">
                      <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Service/product description" required data-testid={`item-desc-${idx}`} />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Input value={item.hsn_code} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} placeholder="HSN/SAC" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <Input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <Input type="number" step="0.01" value={item.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} placeholder="0.00" required data-testid={`item-rate-${idx}`} />
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <Select value={String(item.gst_percent)} onValueChange={v => updateItem(idx, 'gst_percent', parseFloat(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map(r => (
                            <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      {invoiceForm.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-500"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-semibold">&#8377;{calcSubtotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">GST</span><span className="font-semibold">&#8377;{calcGST().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 text-shizah-blue"><span>Grand Total</span><span>&#8377;{(calcSubtotal() + calcGST()).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <Label>Notes (optional)</Label>
              <Textarea value={invoiceForm.notes} onChange={e => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} placeholder="Payment terms, bank details, or any notes..." rows={3} data-testid="invoice-notes" />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={invoiceLoading} className="bg-shizah-blue hover:bg-blue-700 text-white px-8" data-testid="submit-invoice-button">
                {invoiceLoading ? <><Loader2 className="animate-spin mr-2" size={18} />Creating...</> : <><FileText size={18} className="mr-2" />Create Invoice</>}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
